import { Recipe } from '../types/recipe';
import { supabase } from '../lib/supabaseClient';

// ChezAlex house seed for consistent initial generation
const CHEZALEX_HOUSE_SEED = 42069;

const generateRandomSeed = (): number => {
  return Math.floor(Math.random() * 1000000);
};

interface VeniceImageResponse {
  success: boolean;
  imageUrl?: string;
  imageBase64?: string;
  error?: string;
  debugInfo?: any;
}

/**
 * cheflab Food Photography System Prompt Generator
 */
export function generateFoodPhotographyPrompt(recipe: Recipe, keyVisualElements: string): string {
  // Sanitize the recipe title before it enters the image prompt: collapse
  // whitespace/newlines and cap length so a malicious multi-line title can't
  // smuggle line-based instructions into the image generation prompt.
  const title = (recipe.title || 'this dish').replace(/\s+/g, ' ').trim().slice(0, 120);

  const lightingOptions = ['soft natural light', 'dramatic side lighting', 'bright studio lighting', 'warm golden hour light', 'moody ambient light'];
  const angleOptions = ['overhead shot', '45-degree angle shot', 'macro shot of the texture', 'dynamic action shot (e.g., pouring a sauce)', 'lifestyle shot with a hand reaching for the dish'];
  const backgroundOptions = ['a rustic wooden table', 'a clean white marble surface', 'a dark slate background', 'a patterned tablecloth', 'a blurred kitchen scene'];
  const propOptions = ['a vintage silver fork', 'freshly chopped herbs', 'a simple linen napkin', 'a small bowl of a key ingredient', 'a glass of wine'];

  const randomChoice = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

  const lighting = randomChoice(lightingOptions);
  const angle = randomChoice(angleOptions);
  const background = randomChoice(backgroundOptions);
  const props = randomChoice(propOptions);

  const dishDescription = `${title} with ${keyVisualElements}`;

  const templates = [
    `Professional food photography of ${dishDescription}. The scene is captured with ${lighting}, focusing on the dish from an ${angle}. It rests on ${background}, complemented by ${props}. The goal is a commercial food magazine quality image that feels warm and inviting.`,
    `A high-detail, photorealistic image of ${dishDescription}. Styled for a top food blog, the image uses ${lighting} to highlight the food's texture. The camera is positioned for an ${angle}. The setting is ${background}, with ${props} adding to the scene's authenticity.`,
    `Create an appetizing, professional photo of ${dishDescription}. The composition is an ${angle}, illuminated by ${lighting}. The dish is presented on ${background}, with ${props} carefully placed to enhance the story. The final image should be vibrant and sharp.`,
  ];

  const prompt = randomChoice(templates);


  return prompt;
}

/**
 * Generate image using Venice.ai via edge function
 */
export async function generateImageWithVenice(
  recipe: Recipe,
  finalPrompt: string,
  modelId?: string,
  _isCustomPrompt: boolean = false,
  isRegenerate: boolean = false
): Promise<VeniceImageResponse> {
  if (!modelId) {
    return {
      success: false,
      error: 'No image model configured. Please go to Admin > LLM Settings to select an image model.'
    };
  }

  try {
    const seedToUse = isRegenerate ? generateRandomSeed() : CHEZALEX_HOUSE_SEED;


    const { data, error } = await supabase.functions.invoke('venice-proxy', {
      body: {
        action: 'generateImage',
        modelId,
        prompt: finalPrompt,
        options: {
          height: 1024,
          width: 1024,
          seed: seedToUse,
          hideWatermark: true,
        },
      },
    });

    if (error || data?.success === false) {
      let message = data?.error || error?.message || 'Unknown edge function error';
      const ctx = (error as any)?.context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const body = await ctx.json();
          if (body?.error) message = body.error;
        } catch { /* body not JSON, keep default message */ }
      }
      throw new Error(message);
    }

    if (data?.success && data?.imageBase64) {
      return { success: true, imageBase64: data.imageBase64 };
    }

    return {
      success: false,
      error: data?.error || 'Unexpected response from Venice proxy',
      debugInfo: data,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    return {
      success: false,
      error: `Venice.ai image generation failed: ${errorMessage}`,
      debugInfo: error,
    };
  }
}

/**
 * Compress image to ensure it's under 125KB for storage
 */
async function compressImage(imageData: string, maxSizeKB: number = 125): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      let { width, height } = img;
      let quality = 0.9;
      let compressedData: string;

      const compress = () => {
        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        compressedData = canvas.toDataURL('image/jpeg', quality);
        const sizeKB = (compressedData.length * 0.75) / 1024;

        if (sizeKB <= maxSizeKB || quality <= 0.1) {
          resolve(compressedData);
          return;
        }

        if (quality > 0.3) {
          quality -= 0.1;
        } else {
          width = Math.floor(width * 0.9);
          height = Math.floor(height * 0.9);
          quality = 0.8;
        }

        if (width < 200 || height < 200) {
          resolve(compressedData);
          return;
        }

        setTimeout(compress, 10);
      };

      compress();
    };

    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = imageData;
  });
}

export async function downloadImageAsBase64(imageUrl: string): Promise<string | null> {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error(`Failed to download image: ${response.status}`);

    const blob = await response.blob();
    const base64String = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to convert image to base64'));
      reader.readAsDataURL(blob);
    });

    return await compressImage(base64String);
  } catch {
    return null;
  }
}

export async function compressBase64Image(base64Data: string): Promise<string> {
  try {
    return await compressImage(base64Data);
  } catch {
    return base64Data;
  }
}

/**
 * Test Venice.ai connection via edge function
 */
export async function testVeniceConnection(): Promise<{ success: boolean; models?: string[]; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('venice-proxy', {
      body: { action: 'listModels', options: { type: 'image' } },
    });

    if (error) throw new Error(error.message);
    if (!data?.success) throw new Error(data?.error || 'Failed to fetch models');

    const models = data.models?.map((m: any) => m.id) || [];
    return { success: true, models };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

export async function initializeVeniceService(): Promise<void> {
  // No-op: the startup connectivity ping was removed with the logging purge
  // (it burned a real generation call on every app load). Capability probing
  // now happens lazily via the AI gateway on first use.
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  initializeVeniceService().catch(() => undefined);
}
